import { ExternalLink, Tags } from "lucide-react";
import React, { useState } from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { ColumnId } from "./types";
import { LocationData } from "../../../models/LocationData";
import {
  formatNumWithNullNote,
  formatPercWithNullNote,
} from "../../../utils/formatValidation";
import { DemoBadge } from "../../Common/DemoBadge";
import OutsideClickHandler from "../../Common/OutsideClickHandler";
import OverflowBadge from "../../Common/OverflowBadge";
import TableRowActions, {
  TableRowActionItem,
} from "../../Common/TableRowActions";
import ScoreItem from "../../ScoreItem";
import TipsIcon from "../../TipsIcon";
import LabelsList from "../LabelsList";
import PopupSelectLabels from "../PopupSelectLabels";

interface RefactoredFacilityTableItemViewProps {
  item: LocationData;
  organizationId: string;
  isOrgUser: boolean;
  visibleColumns: ColumnId[];
  getTips: (score: string, item: LocationData) => string;
}

const RefactoredFacilityTableItemView: React.FunctionComponent<
  RefactoredFacilityTableItemViewProps
> = ({ item, organizationId, isOrgUser, visibleColumns, getTips }) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`facilityListPage.facilityList.${key}`);
  const gbpLocationIdNum = item.gbp_location_id?.replace("locations/", "");
  const [showLabelsPopup, setShowLabelsPopup] = useState(false);

  const renderCellContent = (columnId: ColumnId) => {
    switch (columnId) {
      case "name":
        return (
          <div className="col-td facility-title" key={columnId}>
            <div className="spacing">
              {item.is_demo && <DemoBadge type="facility" />}
              <NavLink
                to={`/location/${item.id}/${
                  organizationId ? `organization/${organizationId}` : ""
                }`}
                className="blue-link"
                title={item.name || "-"}
              >
                <span className="cell-ellipsis">{item.name || "-"}</span>
              </NavLink>
            </div>
          </div>
        );

      case "average_rating":
        return (
          <div className="col-td reviews-overview" key={columnId}>
            {item?.total_review_count && item.total_review_count > 0 ? (
              <div className="spacing">
                <div className="top-num">{`${item?.average_rating} ⭐`}</div>
              </div>
            ) : (
              <div className="spacing">
                <div className="txt">{t("no_reviews")}</div>
              </div>
            )}
          </div>
        );

      case "total_review_count":
        return (
          <div className="col-td reviews-overview" key={columnId}>
            <div className="spacing">
              <div className="top-num">{item.total_review_count || 0}</div>
            </div>
          </div>
        );

      case "health_index":
        return (
          <div className="col-td score" key={columnId}>
            <div className="spacing">
              <ScoreItem score={item.stat.score} />
              <TipsIcon tipsLabel={getTips(item.stat.score, item)} />
            </div>
          </div>
        );

      case "profile_views":
        return (
          <div className="col-td profile-views" key={columnId}>
            <div className="spacing">
              <div className="top-num">
                {formatNumWithNullNote(item.stat.profile_views)}
              </div>
            </div>
          </div>
        );

      case "profile_views_change":
        return (
          <div className="col-td profile-views" key={columnId}>
            <div className="spacing">
              {formatPercWithNullNote(
                item.stat.profile_views_change_percentage
              )}
            </div>
          </div>
        );

      case "direction_search":
        return (
          <div className="col-td direction-search-count" key={columnId}>
            <div className="spacing">
              <div className="top-num">
                {formatNumWithNullNote(item.stat.direction_search_count)}
              </div>
            </div>
          </div>
        );

      case "direction_search_change":
        return (
          <div className="col-td direction-search-count" key={columnId}>
            <div className="spacing">
              {formatPercWithNullNote(
                item.stat.direction_search_count_change_percentage
              )}
            </div>
          </div>
        );

      case "prefecture":
        return (
          <div className="col-td muni" key={columnId}>
            <div className="spacing">
              <div className="txt cell-ellipsis" title={item.prefecture || "-"}>
                {item.prefecture || "-"}
              </div>
            </div>
          </div>
        );

      case "city":
        return (
          <div className="col-td muni" key={columnId}>
            <div className="spacing">
              <div className="txt cell-ellipsis" title={item.city || "-"}>
                {item.city || "-"}
              </div>
            </div>
          </div>
        );

      case "category": {
        const [firstCategory, ...restCategories] = item.categories || [];
        return (
          <div className="col-td category" key={columnId}>
            <div className="spacing category-summary">
              {firstCategory ? (
                <>
                  <span className="value-chip cell-ellipsis">
                    {firstCategory.name}
                  </span>
                  <OverflowBadge
                    count={restCategories.length}
                    tooltip={restCategories
                      .map((category) => category.name)
                      .join(", ")}
                  />
                </>
              ) : (
                <span>-</span>
              )}
            </div>
          </div>
        );
      }

      case "labels":
        return (
          <div className="col-td label rightmost-label" key={columnId}>
            {isOrgUser ? (
              <div className="spacing">
                <LabelsList
                  locationId={item.id}
                  organizationId={organizationId}
                />
              </div>
            ) : (
              <div className="spacing">-</div>
            )}
          </div>
        );

      case "has_lodging":
        return (
          <div className="col-td lodging" key={columnId}>
            <div className="spacing">
              {item.has_lodging ? t("lodging_hotel") : t("lodging_non_hotel")}
            </div>
          </div>
        );

      case "actions": {
        const actions: TableRowActionItem[] = [];

        if (gbpLocationIdNum) {
          actions.push({
            key: "gbp",
            label: "GBP",
            icon: ExternalLink,
            href: `https://business.google.com/n/${gbpLocationIdNum}/profile`,
            target: "_blank",
            rel: "noreferrer noopener",
            onClick: () => {
              ReactGA.event({
                category: "GBP Link",
                action: "Click to GBP",
                label: "refactor-preview",
              });
            },
          });
        }

        if (isOrgUser) {
          actions.push({
            key: "edit-labels",
            label: t("edit_labels"),
            icon: Tags,
            onClick: () => setShowLabelsPopup((prev) => !prev),
          });
        }

        return (
          <div className="col-td actions" key={columnId}>
            <div className="spacing actions-spacing">
              <TableRowActions actions={actions} />
              {showLabelsPopup && isOrgUser && (
                <OutsideClickHandler
                  onOutsideClick={() => setShowLabelsPopup(false)}
                >
                  <PopupSelectLabels
                    organizationId={organizationId}
                    locationId={item.id}
                    close={() => setShowLabelsPopup(false)}
                  />
                </OutsideClickHandler>
              )}
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="row-td">
      {visibleColumns.map((colId) => renderCellContent(colId))}
    </div>
  );
};

export default RefactoredFacilityTableItemView;
